/**
 * A `<select>` with a filter box, for a list that is COMPLETE in the browser.
 *
 * The bounded counterpart to `CustomerSearchSelect`. The distinction is the
 * whole design and it is not cosmetic:
 *
 *   - A customer's own properties arrive in ONE unpaginated response
 *     (`GET /api/btp/properties?customer_id=…`), so every option is already
 *     here. Filtering locally searches everything, and is honest.
 *   - The customer base does NOT. Filtering a page of it in the browser would
 *     look like search and silently cover a fraction, so that one asks the
 *     server. See `CustomerSearchSelect`.
 *
 * Two components rather than one with a mode flag: the union type would carry
 * an `options`-or-`onSearch` branch through every render path, and the two
 * genuinely behave differently at the edges — this one can say "no match" with
 * certainty, and the other can only ever say "no match in what we fetched".
 *
 * KEEPS THE NATIVE `<select>` rather than becoming a listbox. It is a bounded
 * list, so the browser's own keyboard handling, mobile wheel and accessibility
 * are all free and better than a hand-rolled equivalent. The filter narrows the
 * options; it does not replace the control.
 */
import { useId, useMemo, useState } from 'react';

export interface FilterableSelectOption {
  value: string;
  label: string;
  /** Matched by the filter but not shown — e.g. a code the user might type. */
  keywords?: string;
}

export interface FilterableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: readonly FilterableSelectOption[];
  /** Shown as the first option when nothing is chosen. */
  placeholder: string;
  /** Options this many or fewer skip the filter box — it would be clutter. */
  filterThreshold?: number;
  disabled?: boolean;
  id?: string;
  testId?: string;
  /** Rendered after the select, e.g. a "new hotel" sentinel option. */
  extraOptions?: readonly FilterableSelectOption[];
}

export function FilterableSelect({
  value,
  onChange,
  options,
  placeholder,
  filterThreshold = 8,
  disabled,
  id,
  testId = 'filterable-select',
  extraOptions,
}: FilterableSelectProps) {
  const [q, setQ] = useState('');
  const filterId = useId();

  /*
   * The filter box appears only when the list is long enough to need it. On a
   * customer with three properties it would be furniture, and furniture on a
   * form is what makes forms feel heavy.
   */
  const showFilter = options.length > filterThreshold;

  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) =>
      `${o.label} ${o.keywords ?? ''}`.toLowerCase().includes(term),
    );
  }, [options, q]);

  /*
   * A filtered-out SELECTION stays in the list. Dropping it would silently
   * change what the form submits — the select would fall back to its first
   * option while the user believed their choice stood.
   */
  const selectedMissing =
    value && !shown.some((o) => o.value === value)
      ? options.find((o) => o.value === value)
      : undefined;

  return (
    <>
      {showFilter && (
        <input
          aria-controls={id}
          autoComplete="off"
          className="input"
          data-testid={`${testId}-filter`}
          disabled={disabled}
          id={filterId}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Filter ${options.length.toLocaleString()} options…`}
          style={{ marginBottom: 4 }}
          value={q}
        />
      )}

      <select
        className="select"
        data-testid={testId}
        disabled={disabled}
        id={id}
        onChange={(e) => onChange(e.target.value)}
        value={value}
      >
        <option value="">{placeholder}</option>
        {selectedMissing && (
          <option key={selectedMissing.value} value={selectedMissing.value}>
            {selectedMissing.label}
          </option>
        )}
        {shown.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
        {extraOptions?.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>

      {/* Says when the filter has hidden everything, rather than presenting an
          empty control as "this customer has no hotels". */}
      {showFilter && q.trim() !== '' && shown.length === 0 && (
        <p
          className="detail-note"
          data-testid={`${testId}-no-match`}
          style={{ fontSize: 11, margin: '4px 0 0' }}
        >
          <span className="muted">
            Nothing matches &ldquo;{q.trim()}&rdquo; — {options.length} option
            {options.length === 1 ? '' : 's'} are hidden by the filter.
          </span>
        </p>
      )}
    </>
  );
}
